import { useEffect, useMemo, useState } from "react";

import {
  ArchiveBoxIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  CubeIcon,
  CurrencyPoundIcon,
  ExclamationTriangleIcon,
  InboxStackIcon,
} from "@heroicons/react/24/outline";

import HeadlineStat from "../../components/shared/HeadlineStat";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import ReportLayout, { ReportFilterSelect } from "./ReportLayout";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import {
  PART_CATEGORIES,
  STOCK_STATUSES,
  buildInventory,
} from "./reportsMockData";

const PAGE_SIZE = 75;
const fmtInt = (n: number) => n.toLocaleString("en-GB");

/**
 * Inventory Report — current parts-in-stock snapshot. Shows top-line totals
 * (value, quantity, aged stock, missing parts), a category breakdown, and
 * the line-item table with filters.
 *
 * Mock-backed until the `/report/inventory` endpoint ships.
 */
export default function InventoryReport() {
  const { setInfo } = usePageNameContext();
  const [categoryId, setCategoryId] = useState("all");
  const [status, setStatus] = useState<string>("all");
  const [age, setAge] = useState<string>("all");
  const [page, setPage] = useState(0);

  useEffect(() => {
    setInfo({
      name: "Inventory Report",
      desc: "Parts in stock right now — summary tiles, category breakdown and line-item table",
    });
  }, [setInfo]);

  const inventory = useMemo(() => buildInventory(), []);

  const filtered = useMemo(() => {
    return inventory.filter((row) => {
      if (categoryId !== "all" && row.partCategoryId !== categoryId) return false;
      if (status !== "all" && row.status !== status) return false;
      if (age === "recent" && row.daysInStock > 30) return false;
      if (age === "stale" && row.daysInStock < 90) return false;
      if (age === "dead" && row.daysInStock < 180) return false;
      return true;
    });
  }, [inventory, categoryId, status, age]);

  useEffect(() => {
    setPage(0);
  }, [categoryId, status, age]);

  const totals = useMemo(() => {
    let units = 0;
    let value = 0;
    let lines = filtered.length;
    let missing = 0;
    let reserved = 0;
    let stale = 0;
    for (const r of filtered) {
      units += r.quantity;
      value += r.totalValue;
      if (r.status === "Missing") missing += r.quantity;
      if (r.status === "Reserved") reserved += r.quantity;
      if (r.daysInStock > 90) stale += r.quantity;
    }
    return { units, value, lines, missing, reserved, stale };
  }, [filtered]);

  const avgUnitValue =
    totals.units === 0 ? 0 : Math.round(totals.value / totals.units);

  // Per-category breakdown — used in the "Stock by category" panel below
  // the tiles. Always derived from the unfiltered inventory so the
  // summary stays representative when the user filters the table.
  const byCategory = useMemo(() => {
    const map = new Map<
      string,
      { name: string; units: number; value: number; lines: number }
    >();
    for (const row of inventory) {
      const key = row.partCategory;
      const existing = map.get(key) ?? {
        name: key,
        units: 0,
        value: 0,
        lines: 0,
      };
      existing.units += row.quantity;
      existing.value += row.totalValue;
      existing.lines += 1;
      map.set(key, existing);
    }
    return [...map.values()].sort((a, b) => b.value - a.value);
  }, [inventory]);

  const maxCategoryValue =
    byCategory.reduce((m, r) => Math.max(m, r.value), 0) || 1;

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page]
  );

  const csvRows = useMemo(
    () =>
      filtered.map((r) => ({
        sku: r.sku,
        partName: r.partName,
        partCategory: r.partCategory,
        donorStockNumber: r.vehicle.stockNumber,
        donorVehicle: `${r.vehicle.year} ${r.vehicle.make} ${r.vehicle.model}`,
        bin: r.bin,
        quantity: r.quantity,
        unitPriceGBP: r.unitPrice,
        totalValueGBP: r.totalValue,
        status: r.status,
        addedOn: r.addedOn,
        daysInStock: r.daysInStock,
      })),
    [filtered]
  );

  return (
    <ReportLayout
      title="Inventory Report"
      description="Live picture of every part in stock — value, ageing and location — with a category breakdown below."
      icon={InboxStackIcon}
      controls={
        <>
          <ReportFilterSelect
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            options={[
              { value: "all", label: "All categories" },
              ...PART_CATEGORIES.map((c) => ({ value: c.id, label: c.name })),
            ]}
            width="11rem"
          />
          <ReportFilterSelect
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: "All statuses" },
              ...STOCK_STATUSES.map((s) => ({ value: s, label: s })),
            ]}
            width="9rem"
          />
          <ReportFilterSelect
            label="Age"
            value={age}
            onChange={setAge}
            options={[
              { value: "all", label: "All ages" },
              { value: "recent", label: "Recent (≤ 30d)" },
              { value: "stale", label: "Stale (≥ 90d)" },
              { value: "dead", label: "Dead (≥ 180d)" },
            ]}
            width="10rem"
          />
        </>
      }
      csvRows={csvRows}
      csvFilename="inventory"
      headlineStats={
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <HeadlineStat
            tone="blue"
            icon={InboxStackIcon}
            value={fmtInt(totals.lines)}
            label="Stock lines"
            sublabel={`${fmtInt(totals.units)} units`}
          />
          <HeadlineStat
            tone="emerald"
            icon={CurrencyPoundIcon}
            value={PriceFormat(totals.value)}
            label="Total stock value"
            sublabel="At unit prices"
          />
          <HeadlineStat
            tone="violet"
            icon={CubeIcon}
            value={PriceFormat(avgUnitValue)}
            label="Avg unit value"
            sublabel="Value / units"
          />
          <HeadlineStat
            tone="amber"
            icon={ArchiveBoxIcon}
            value={fmtInt(totals.stale)}
            label="Stale units"
            sublabel="> 90 days in stock"
          />
          <HeadlineStat
            tone="cyan"
            icon={BanknotesIcon}
            value={fmtInt(totals.reserved)}
            label="Reserved"
            sublabel="Awaiting dispatch"
          />
          <HeadlineStat
            tone="rose"
            icon={ExclamationTriangleIcon}
            value={fmtInt(totals.missing)}
            label="Missing"
            sublabel="Flagged, not yet located"
          />
        </div>
      }
    >
      {/* Category breakdown — gives a quick "where is the value?" read
          before the user scrolls into the full table. */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <header className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <ArrowTrendingUpIcon className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Stock value by category
              </h3>
              <p className="text-[11px] text-slate-500">
                Ordered by total inventory value
              </p>
            </div>
          </div>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
            {byCategory.length} categories
          </span>
        </header>

        <ul className="grid grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
          {byCategory.map((row) => {
            const pct = (row.value / maxCategoryValue) * 100;
            return (
              <li key={row.name} className="space-y-1">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="h-1.5 w-1.5 flex-none rounded-full bg-primary-500"
                      aria-hidden
                    />
                    <span className="truncate font-medium text-slate-700">
                      {row.name}
                    </span>
                  </span>
                  <span className="flex flex-none items-baseline gap-2 tabular-nums">
                    <span className="text-[10px] text-slate-400">
                      {fmtInt(row.units)} u · {fmtInt(row.lines)} lines
                    </span>
                    <span className="font-semibold text-slate-900">
                      {PriceFormat(row.value)}
                    </span>
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-[width] duration-500"
                    style={{
                      width: `${Math.max(pct, row.value > 0 ? 4 : 0)}%`,
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th>SKU</Th>
                <Th>Part</Th>
                <Th>Category</Th>
                <Th>Donor vehicle</Th>
                <Th>Bin</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Unit value</Th>
                <Th className="text-right">Total value</Th>
                <Th>Status</Th>
                <Th className="text-right">Days</Th>
                <Th>Added</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-slate-500">
                    {r.sku}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{r.partName}</td>
                  <td className="px-3 py-2 text-slate-500">{r.partCategory}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="flex flex-col leading-tight">
                      <span className="text-slate-700">
                        {r.vehicle.year} {r.vehicle.make} {r.vehicle.model}
                      </span>
                      <span className="font-mono text-[11px] text-slate-400">
                        {r.vehicle.registration}
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-600">
                      {r.bin}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                    {r.quantity}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                    {PriceFormat(r.unitPrice)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                    {PriceFormat(r.totalValue)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <StatusPill status={r.status} />
                  </td>
                  <td
                    className={classNames(
                      "whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums",
                      r.daysInStock > 180
                        ? "text-rose-600"
                        : r.daysInStock > 90
                          ? "text-amber-600"
                          : "text-slate-700"
                    )}
                  >
                    {r.daysInStock}d
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                    {r.addedOn}
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    No stock matches the current filter.
                  </td>
                </tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-slate-50">
                <tr className="text-sm font-semibold text-slate-700">
                  <td className="px-3 py-2" colSpan={5}>
                    Totals
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtInt(totals.units)}
                  </td>
                  <td />
                  <td className="px-3 py-2 text-right tabular-nums">
                    {PriceFormat(totals.value)}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-sm">
            <p className="text-slate-500">
              Showing{" "}
              <span className="font-semibold text-slate-700">
                {fmtInt(page * PAGE_SIZE + 1)}–
                {fmtInt(Math.min((page + 1) * PAGE_SIZE, filtered.length))}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-700">
                {fmtInt(filtered.length)}
              </span>
            </p>
            <div className="flex items-center gap-1">
              <PageButton
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Prev
              </PageButton>
              <span className="px-2 text-slate-500">
                Page {page + 1} / {pageCount}
              </span>
              <PageButton
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1}
              >
                Next
              </PageButton>
            </div>
          </div>
        )}
      </div>
    </ReportLayout>
  );
}

function Th({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={classNames(
        "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500",
        className ?? ""
      )}
    >
      {children}
    </th>
  );
}

function PageButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className={classNames(
        "rounded-md border px-2 py-1 text-xs font-semibold transition",
        rest.disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
          : "border-slate-300 bg-white text-slate-700 hover:border-primary-300 hover:text-primary-700"
      )}
    >
      {children}
    </button>
  );
}

const STATUS_TONES: Record<string, string> = {
  "In Stock":  "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Reserved:    "bg-amber-50 text-amber-700 ring-amber-200",
  "On Sale":   "bg-blue-50 text-blue-700 ring-blue-200",
  Missing:     "bg-rose-50 text-rose-700 ring-rose-200",
};

function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? "bg-slate-50 text-slate-600 ring-slate-200";
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
        tone
      )}
    >
      {status}
    </span>
  );
}
